import * as React from 'react'

import {
  Box,
  Button,
  NoticeBox,
  Text,
  Title,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Loading from 'modules/core/components/Loading'
import SelectField from 'modules/core/components/SelectField'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { type TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import { type PolicyTimelines } from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'
import type PolicyTimeline from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import List from './List'
import UpsertAssignment from './UpsertAssignment'

interface Props {
  employee: Employee
  selectedTimelineItem: PolicyTimelineItem
  setSelectedTimelineItem: (item: PolicyTimelineItem) => void
  policies: TimeoffPolicies
  policyTimelines: PolicyTimelines
}

const LearnMoreLink = () => (
  <Box paddingLeft="s32" paddingTop="s12">
    <HelpCenterLink name="timeoff_policy_timeline">
      <Text.Normal underlined weight="semibold">
        {i18n.t('time_off.policy_timeline.learn_more')}
      </Text.Normal>
    </HelpCenterLink>
  </Box>
)

function PolicyAssignments({
  employee,
  selectedTimelineItem,
  setSelectedTimelineItem,
  policies,
  policyTimelines,
}: Props) {
  const [showSidePanel, toggleSidePanel] = useToggle(false)
  const [showUpsertForm, toggleUpsertForm] = useToggle(false)

  const policyKey = 'policy_assignment.edit'

  const { loading } = useAsyncData(
    () => policyAuthorizations.authorizePolicies(policyKey, [employee]),
    [employee.id]
  )

  const canManageAssignments = policyAuthorizations.for(policyKey, employee)

  const [timeline, setTimeline] = React.useState<PolicyTimeline | null | undefined>()

  useEffect(() => {
    const firstTimeline = policyTimelines.at(0)
    if (firstTimeline && 'current' in firstTimeline) {
      setTimeline(firstTimeline)
      setSelectedTimelineItem(firstTimeline.current)
    }
  }, [employee.id, timeline, setSelectedTimelineItem])

  let policyOptions: SelectOption[] = []

  if (timeline) {
    policyOptions = timeline.items.map((item) => {
      return {
        label: `${i18n.t('contract_versions.header.effective_on')} ${item.start_at}`,
        value: item.policy_assignment_id ? item.policy_assignment_id : '0', // reason to be 0 in line 103
      }
    })
    policyOptions.unshift({
      disabled: true,
      label: i18n.t('time_off.policy_timeline.descriptions.current_assignment'),
      value: -1, // We just use a negative value since this option can't be selected and values should be unique since we use them as keys
    })
    policyOptions.splice(2, 0, {
      disabled: true,
      label: i18n.t('time_off.policy_timeline.descriptions.previous_assignments'),
      value: -2, // We just use a negative value since this option can't be selected and values should be unique since we use them as keys
    })
  }

  const fetchTimeline = async () => {
    await policyTimelines.fetch({
      data: {
        employee_id: employee.id,
      },
    })
    const firstTimeline = policyTimelines.at(0)
    if (firstTimeline && 'current' in firstTimeline) {
      setTimeline(firstTimeline)
      setSelectedTimelineItem(firstTimeline.current)
    }
  }

  const onSelectTimelineItem = (item: PolicyTimelineItem) => {
    if (item.policy_assignment_id !== selectedTimelineItem.policy_assignment_id) {
      setSelectedTimelineItem(item)
    }
    toggleUpsertForm(false)()
  }

  const onSelectAssignment = (value: string) => {
    const item = timeline?.items.find((it) => {
      // This is the case for the "Default" assignment (which is virtual and has no id)
      if (value === '0') return it.policy_assignment_id === null
      return it.policy_assignment_id === parseInt(value)
    })
    if (item) setSelectedTimelineItem(item)
  }

  const selectedAssignmentTitle = () => {
    if (timeline?.current?.policy_assignment_id === selectedTimelineItem?.policy_assignment_id) {
      return i18n.t('time_off.policy_timeline.descriptions.current_assignment')
    } else if (selectedTimelineItem?.isFuture) {
      return i18n.t('time_off.policy_timeline.descriptions.future_assignments')
    } else {
      return i18n.t('time_off.policy_timeline.descriptions.previous_assignments')
    }
  }

  const isLastAssignment =
    timeline?.items[0]?.policy_assignment_id === selectedTimelineItem?.policy_assignment_id

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <Box>
        <Title
          variant="500"
          illustration={'group'}
          title={selectedAssignmentTitle()}
          description={
            isLastAssignment
              ? i18n.t('common.from_date', {
                  date: formatBackendDateToDayMonthYear(selectedTimelineItem?.start_at, true),
                })
              : i18n.t('time_off.policy_timeline.item_range', {
                  start_on: formatBackendDateToDayMonthYear(selectedTimelineItem?.start_at, true),
                  finish_on: formatBackendDateToDayMonthYear(selectedTimelineItem?.end_at, true),
                })
          }
        />
        {!canManageAssignments && timeline?.items && timeline?.items?.length > 1 && (
          <SelectField
            label=""
            options={policyOptions}
            onChange={(value) => onSelectAssignment(value)}
          />
        )}
        {canManageAssignments && (
          <Button onClick={toggleSidePanel(true)} type="secondary">
            {i18n.t('time_off.policy_timeline.list_assignments')}
          </Button>
        )}
      </Box>
      <Sidepanel
        width="wide"
        open={showSidePanel}
        title={i18n.t('time_off.policy_timeline.title')}
        onClose={toggleSidePanel(false)}
        closeLabel={i18n.t('common.close')}
        boxPadding={false}
      >
        <NoticeBox
          key="warning"
          message={
            <Box maxWidth="s400">
              <Text.Normal>{i18n.t('time_off.policy_timeline.warning_message')}</Text.Normal>
            </Box>
          }
          paddingX="s4"
          color="grey400"
          component={<LearnMoreLink />}
        />
        {timeline && (
          <List
            timelineItems={timeline.items}
            currentAssignmentId={timeline.current?.policy_assignment_id}
            selectedTimelineItem={selectedTimelineItem}
            onSelectTimelineItem={onSelectTimelineItem}
            employee={employee}
            fetchTimeline={fetchTimeline}
            policies={policies}
            policyTimelines={policyTimelines}
          ></List>
        )}
        {showUpsertForm ? (
          <Box paddingTop="s16">
            <UpsertAssignment
              employee={employee}
              toggleUpsertForm={toggleUpsertForm}
              fetchTimeline={fetchTimeline}
              isDefaultAssignment={false}
              policies={policies}
              policyTimelines={policyTimelines}
            />
          </Box>
        ) : (
          <Box alignItems="flexEnd" paddingTop="s32" paddingRight="s16">
            <Button onClick={toggleUpsertForm(true)} type="secondary" iconName="add">
              {i18n.t('time_off.policy_timeline.create_assignment.title')}
            </Button>
          </Box>
        )}
      </Sidepanel>
    </>
  )
}

export default PolicyAssignments
